import logger from '@/utils/logger';
import { createCache, CacheStrategy } from '@/lib/core/CacheManager';
/**
 * 统一内容检测引擎
 * 使用相同的检测逻辑处理敏感词过滤和联想问题触发
 */

// 将import移到顶部
import {
  getSensitiveWordsConfig,
  getSensitivePatternsConfig,
  getFilterConfig,
} from '@/config/contentConfig';

// 移除内嵌建议库，统一从配置文件与场景配置读取

// 类型定义
interface DetectionResult {
  matches: string[];
  score: number;
  items: any[];
}

interface SuggestionResult {
  filteredText: string;
  hasSensitive: boolean;
  sensitiveWords: string[];
  riskLevel: 'low' | 'medium' | 'high';
}

/**
 * 通用内容检测类
 */
class ContentDetector {
  // 使用统一缓存管理器
  private regexCache = createCache<RegExp | RegExp[]>('content-detector', {
    strategy: CacheStrategy.MEMORY,
    maxSize: 20,
    ttl: 30 * 60 * 1000, // 30分钟
  });

  private lastWordsConfig: string[] | null = null;

  private lastPatternsConfig: any[] | null = null;

  // 日志降噪：记录上一次输出签名与时间，做限频与重复过滤
  private lastLogSignature: string | null = null;

  private lastLogTimeMs = 0;

  private buildRegexFromWords(words?: string[]): RegExp {
    if (!words) words = getSensitiveWordsConfig();
    const cacheKey = 'word-regex';

    // 检查缓存是否有效
    if (this.lastWordsConfig && JSON.stringify(this.lastWordsConfig) === JSON.stringify(words)) {
      const cached = this.regexCache.get(cacheKey) as RegExp;
      if (cached) {
        return cached;
      }
    }

    if (words.length === 0) {
      const emptyRegex = /(?!)/ as RegExp; // 永不匹配的正则
      this.regexCache.set(cacheKey, emptyRegex);
      this.lastWordsConfig = words;
      return emptyRegex;
    }

    const filterConfig = getFilterConfig();
    const escapedWords = words
      .map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .sort((a, b) => b.length - a.length); // 长词优先匹配

    // 英文词边界（仅英文），中文不加边界
    const wordPattern = escapedWords
      .map((w) => {
        if (filterConfig.englishWordBoundary && /[a-z]/i.test(w) && !/[\u4e00-\u9fa5]/.test(w)) {
          return `\\b${w}\\b`;
        }
        return w;
      })
      .join('|');

    const regex = new RegExp(`(${wordPattern})`, 'gi');
    this.regexCache.set(cacheKey, regex);
    this.lastWordsConfig = words;
    return regex;
  }

  private buildRegexFromPatterns(patterns?: any[]): RegExp[] {
    if (!patterns) patterns = getSensitivePatternsConfig();
    const cacheKey = 'pattern-regex';

    // 检查缓存是否有效
    if (
      this.lastPatternsConfig &&
      JSON.stringify(this.lastPatternsConfig) === JSON.stringify(patterns)
    ) {
      const cached = this.regexCache.get(cacheKey) as RegExp[];
      if (cached) {
        return cached;
      }
    }

    const regexPatterns = patterns.map((pattern) => {
      if (pattern instanceof RegExp) return pattern;
      try {
        return new RegExp(pattern.regex, pattern.flags || 'g');
      } catch (error) {
        logger.warn(`[ContentDetector] Invalid regex pattern: ${pattern.regex}`, error);
        return /(?!)/ as RegExp;
      }
    });

    this.regexCache.set(cacheKey, regexPatterns);
    this.lastPatternsConfig = patterns;
    return regexPatterns;
  }

  /**
   * 清除缓存（当配置更新时调用）
   */
  clearCache(): void {
    this.regexCache.clear();
    this.lastWordsConfig = null;
    this.lastPatternsConfig = null;
    logger.debug('[ContentDetector] 缓存已清除');
  }

  /**
   * 获取缓存统计
   */
  getCacheStats() {
    return this.regexCache.getStats();
  }

  /**
   * 检测文本中的敏感词
   */
  detectSensitiveWords(text: string): DetectionResult {
    const filterConfig = getFilterConfig();
    if (!text || !filterConfig.enabled) {
      return { matches: [], score: 0, items: [] };
    }
    const foundWords: string[] = [];
    let totalScore = 0;

    // 规范化：NFKC + 全角转半角 + 大小写折叠 + 标点统一/空白
    const normalized = this.normalizeText(text);

    const wordRegex = this.buildRegexFromWords();
    const wordMatches = normalized.match(wordRegex);
    if (wordMatches) {
      foundWords.push(...wordMatches);
      // 按权重与长度加分
      const weights = (filterConfig.weights || ({} as any)) as any;
      const defaultWordScore = (weights.defaultWordScore ?? 2) as number;
      const longWordBonus = (weights.longWordBonus ?? 1) as number;
      const lengthThreshold = (weights.wordLengthBonusThreshold ?? 4) as number;
      totalScore += wordMatches.reduce((acc, w) => {
        const bonus = w.length >= lengthThreshold ? longWordBonus : 0;
        return acc + defaultWordScore + bonus;
      }, 0);
    }
    const patterns = this.buildRegexFromPatterns();
    patterns.forEach((pattern) => {
      const matches = normalized.match(pattern);
      if (matches) {
        foundWords.push(...matches.map(() => '[隐私信息]'));
        const defaultPatternScore = (filterConfig.weights?.defaultPatternScore ?? 3) as number;
        totalScore += matches.length * defaultPatternScore;
      }
    });
    return {
      matches: [...new Set(foundWords)],
      score: totalScore,
      items: foundWords,
    };
  }

  /**
   * 过滤敏感内容
   */
  filterSensitiveContent(text: string): string {
    const filterConfig = getFilterConfig();
    if (!text || !filterConfig.enabled) return text;
    const maskChar = filterConfig.maskChar || '*';
    const strategy = filterConfig.filterStrategy || 'mask-equal';
    const fixedMask = filterConfig.fixedMask || '***';

    const replaceWith = (match: string) => {
      if (strategy === 'mask-fixed') return fixedMask;
      if (strategy === 'block') return '';
      return maskChar.repeat(match.length);
    };

    let filteredText = text;
    const wordRegex = this.buildRegexFromWords();
    filteredText = filteredText.replace(wordRegex, (match) => replaceWith(match));
    const patterns = this.buildRegexFromPatterns();
    patterns.forEach((pattern) => {
      filteredText = filteredText.replace(pattern, (match) => replaceWith(match));
    });
    return filteredText;
  }

  /**
   * 智能内容分析（主要对外接口）
   */
  analyzeContent(
    text: string,
    options?: {
      enableSensitiveFilter?: boolean;
      conversationHistory?: any[];
      context?: any;
      sceneConfig?: any;
    }
  ): SuggestionResult {
    const filterConfig = getFilterConfig();
    if (!text || !filterConfig.enabled) {
      return {
        filteredText: text,
        hasSensitive: false,
        sensitiveWords: [],
        riskLevel: 'low',
      };
    }

    // 检查是否只过滤用户消息
    const isUserMessage = options?.context?.isUserMessage ?? false;
    const shouldFilter = !filterConfig.filterUserOnly || isUserMessage;

    const sensitiveResult = this.detectSensitiveWords(text);
    const hasSensitive = sensitiveResult.matches.length > 0;
    let riskLevel: 'low' | 'medium' | 'high' = 'low';
    if (sensitiveResult.matches.length > 3) {
      riskLevel = 'high';
    } else if (sensitiveResult.matches.length > 1) {
      riskLevel = 'medium';
    } else if (sensitiveResult.matches.length > 0) {
      riskLevel = 'medium';
    }

    // 只有在应该过滤的情况下才进行内容过滤
    const filteredText = shouldFilter ? this.filterSensitiveContent(text) : text;
    // 仅在“最终定稿”阶段输出日志，且进行限频+去重（从配置读取限频窗口与是否仅final）。
    // 对于用户消息（isUserMessage），无论是否定稿都允许记录，确保及时监控。
    const isFinal = Boolean(options?.context?.definite ?? options?.context?.isFinal);
    const isUserMsg = Boolean(options?.context?.isUserMessage);
    const now = Date.now();
    const throttleMs = getFilterConfig().logThrottleMs ?? 2000;
    const signature = `${riskLevel}|${sensitiveResult.matches.join(',')}`;

    const phaseAllowed = isUserMsg ? true : !filterConfig.logOnlyFinal || isFinal;
    if (filterConfig.logSensitive && hasSensitive && phaseAllowed) {
      const isSameAsLast = this.lastLogSignature === signature;
      const withinThrottle = now - this.lastLogTimeMs < throttleMs;
      if (!isSameAsLast || !withinThrottle) {
        logger.warn('[ContentDetector] 检测到敏感内容：', {
          originalLength: text.length,
          sensitiveWords: sensitiveResult.matches,
          riskLevel,
          context: options?.context || {},
        });
        this.lastLogSignature = signature;
        this.lastLogTimeMs = now;
      }
    }
    // 联想建议逻辑（支持场景配置）
    return {
      filteredText,
      hasSensitive,
      sensitiveWords: sensitiveResult.matches,
      riskLevel,
    };
  }

  // 文本规范化（中文保留，英文小写，统一空白/标点，全角->半角）
  private normalizeText(input: string): string {
    if (!input) return '';
    let s = input.normalize('NFKC');
    // 全角转半角：只转换常见范围
    s = s.replace(/[\uFF01-\uFF5E]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0));
    // 大小写折叠
    s = s.toLowerCase();
    // 统一标点为空格
    s = s.replace(/[\p{P}\p{S}]+/gu, ' ');
    // 合并空白
    s = s.replace(/\s+/g, ' ').trim();
    return s;
  }
}

// 创建全局实例
const contentDetector = new ContentDetector();

// 导出便捷方法
export const detectSensitiveWords = (text: string) => contentDetector.detectSensitiveWords(text);

export const filterSensitiveContent = (text: string) =>
  contentDetector.filterSensitiveContent(text);

export const analyzeContent = (text: string, options?: any) =>
  contentDetector.analyzeContent(text, options);

// 导出类型
export type { SuggestionResult };

// 导出实例
export default contentDetector;
